import {
  useCallback, 
  useContext, 
  useEffect, 
  useState 
} from "react"

import { 
  DataTableSessionHeader,
  EventMenu,
  EventPageHeader, 
  EventViewsTracker,
  Page,
  Timetable,
} from "@components"

import { UserContext } from "@context"
import { hasFullLiveAccess, mySqlToLocal } from "@helpers"

import { 
  useSessionSelectorSidebar,
  useSetActiveSidebarLink,
  useSetCurrentEvent, 
  useTimetableSummary 
} from "@hooks"

import { Card, CardHeader } from "@heroui/react"
import { Squares2X2Icon } from "@heroicons/react/24/outline"
import dayjs from "dayjs"
import { isEmpty } from "lodash"
import { useParams } from "react-router-dom"

import Stats from "./Stats"

const ManageTimetable = () => {
  const { event: eventId } = useParams()
  const { user, isLoadingCurrentEvent } = useContext(UserContext)
  const [currentSession, setCurrentSession] = useState(null)
   
  /**
   * Hooks
   */
  useSetActiveSidebarLink("EVENT_MANAGE_OVERVIEW", "EVENT_MANAGE")
  useSetCurrentEvent(eventId)
  const { data: timetableSummary, refetch: refetchTimetableSummary } = useTimetableSummary(currentSession?.id)
  const { openSessionSelectorSidebar, closeSessionSelectorSidebar } = useSessionSelectorSidebar()

  /**
   * Effects
   */
  useEffect(() => {
    if (!isLoadingCurrentEvent && user?.currentEvent?.sessions?.length) {
      setCurrentSession(user.currentEvent.sessions[0])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoadingCurrentEvent, user]);

  /**
   * Functions 
   */
  const handleChangeSession = (session) => {
    setCurrentSession(session)
    closeSessionSelectorSidebar()
  }
  
  const handleRefreshSummary = useCallback(() => {
    refetchTimetableSummary()
  }, [refetchTimetableSummary])

  const handleShowSessionsSidebar = () => {
    openSessionSelectorSidebar({ sessions: user.currentEvent.sessions, handleChangeSession })
  }

  const isReadOnlyMode = !isEmpty(currentSession) ? mySqlToLocal(currentSession.ends_at) < dayjs() : true
  
  return (
    <EventViewsTracker eventId={eventId}>
      <Page>
        <EventPageHeader
          title="Overview"
          icon={<Squares2X2Icon className="page-header-icon" />}
          event={user?.currentEvent}
          actions={hasFullLiveAccess() && <EventMenu event={user?.currentEvent} user={user} />}
        />

        <Stats summary={timetableSummary} />
        
        <Card fullWidth className="mt-4 shadow-lg grid grid-rows-[auto,1fr] min-h-[calc(100vh-(268px))]">
          <CardHeader className="text-white bg-indigo-800">
            <DataTableSessionHeader 
              session={currentSession} 
              showChangeSessionButton={user?.currentEvent?.sessions?.length > 1} 
              handleShowSessionsSidebar={handleShowSessionsSidebar} 
            />
          </CardHeader>

          <Timetable 
            currentSession={currentSession}
            event={user?.currentEvent}
            handleRefreshed={handleRefreshSummary}
            isReadOnlyMode={isReadOnlyMode}
            isReady={!isLoadingCurrentEvent && !isEmpty(currentSession)}
          /> 
        </Card>
      </Page>
    </EventViewsTracker>
  )
}

export default ManageTimetable
